/*
 *
 * Copyright (c) 2025 Alexander Orlov.
 * 34 Middletown Ave Atlantic Highlands NJ 07716
 *
 * THIS SOFTWARE IS THE CONFIDENTIAL AND PROPRIETARY INFORMATION OF
 * Alexander Orlov. ("CONFIDENTIAL INFORMATION"). YOU SHALL NOT DISCLOSE
 * SUCH CONFIDENTIAL INFORMATION AND SHALL USE IT ONLY IN ACCORDANCE
 * WITH THE TERMS OF THE LICENSE AGREEMENT YOU ENTERED INTO WITH
 * Alexander Orlov.
 *
 * Author: Alexander Orlov Aegis AO Soft
 *
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

// Only English is bundled eagerly — it's the fallback (so the UI never shows raw keys)
// and the default, so the app renders instantly. The other 12 locales are code-split:
// each is fetched on demand the first time that language is selected. This keeps ~3 MB
// of translation data OUT of the main bundle (locales total ~3.2 MB raw / up to 385 KB each).
import en from './locales/en/common.json';

const LANGUAGE_KEY = 'aegis-language';

/**
 * GUI languages written right-to-left. The app chrome itself is still laid out left-to-right, so this is
 * used to set `dir` on the places that render language text — otherwise Persian and Urdu are laid out as
 * an LTR paragraph: aligned to the left edge, with list markers and sentence-final punctuation on the
 * wrong side and the caret in a text box starting on the wrong end.
 */
export const RTL_LANGUAGES = ['fa', 'ur', 'ar', 'he'];

/** 'rtl' for a right-to-left language code (region suffixes tolerated), otherwise 'ltr'. */
export const dirForLanguage = (code?: string | null): 'rtl' | 'ltr' =>
  RTL_LANGUAGES.includes((code || '').trim().toLowerCase().split('-')[0]) ? 'rtl' : 'ltr';

/**
 * The CANONICAL GUI language list — the single source of truth for the whole owner-web app:
 * the language selector, i18n, AND the storefront Design auto-translation targets all read from here.
 * Add a language here (plus its locale file + loader below) and it is automatically offered for
 * translation too — no other code or backend change needed. `name` is the English name sent to the
 * translation backend as the prompt target.
 */
export interface GuiLanguage {
  code: string;
  name: string;
  nativeName: string;
  flag: string;
}

export const GUI_LANGUAGES: GuiLanguage[] = [
  { code: 'en', name: 'English', nativeName: 'English', flag: '🇺🇸' },
  { code: 'es', name: 'Spanish', nativeName: 'Español', flag: '🇪🇸' },
  { code: 'de', name: 'German', nativeName: 'Deutsch', flag: '🇩🇪' },
  { code: 'ru', name: 'Russian', nativeName: 'Русский', flag: '🇷🇺' },
  { code: 'fr', name: 'French', nativeName: 'Français', flag: '🇫🇷' },
  { code: 'pt', name: 'Portuguese', nativeName: 'Português', flag: '🇵🇹' },
  { code: 'tr', name: 'Turkish', nativeName: 'Türkçe', flag: '🇹🇷' },
  { code: 'uk', name: 'Ukrainian', nativeName: 'Українська', flag: '🇺🇦' },
  { code: 'fa', name: 'Persian', nativeName: 'فارسی', flag: '🇮🇷' },
  { code: 'hy', name: 'Armenian', nativeName: 'Հայերեն', flag: '🇦🇲' },
  { code: 'ka', name: 'Georgian', nativeName: 'ქართული', flag: '🇬🇪' },
  { code: 'ur', name: 'Urdu', nativeName: 'اردو', flag: '🇵🇰' },
  { code: 'uz', name: 'Uzbek', nativeName: "O'zbek", flag: '🇺🇿' },
  { code: 'zh-Hans', name: 'Simplified Chinese', nativeName: '简体中文', flag: '🇨🇳' },
  { code: 'zh-Hant', name: 'Traditional Chinese', nativeName: '繁體中文', flag: '🇹🇼' },
];

/**
 * The languages the **myezwheels storefront** (the public site, a separate app) can actually display —
 * its own i18n set is `en, es, pt, fr, de, it, ru, zh-CN, zh-TW`. Mirrored here with the CANONICAL
 * translation codes the DB/admin use (Chinese as `zh-Hans`/`zh-Hant`; the storefront aliases
 * `zh-CN`/`zh-TW` → these). This — NOT {@link GUI_LANGUAGES} (the 15 admin-UI languages) — is the set the
 * Storefront Designer offers for "Add language" and uses as storefront-content translation targets, so
 * an owner never adds a language the storefront can't show. Note: `it` is NOT an admin-UI language.
 */
export const STOREFRONT_LANGUAGES: GuiLanguage[] = [
  { code: 'en', name: 'English', nativeName: 'English', flag: '🇺🇸' },
  { code: 'es', name: 'Spanish', nativeName: 'Español', flag: '🇪🇸' },
  { code: 'pt', name: 'Portuguese', nativeName: 'Português', flag: '🇵🇹' },
  { code: 'fr', name: 'French', nativeName: 'Français', flag: '🇫🇷' },
  { code: 'de', name: 'German', nativeName: 'Deutsch', flag: '🇩🇪' },
  { code: 'it', name: 'Italian', nativeName: 'Italiano', flag: '🇮🇹' },
  { code: 'ru', name: 'Russian', nativeName: 'Русский', flag: '🇷🇺' },
  { code: 'zh-Hans', name: 'Simplified Chinese', nativeName: '简体中文', flag: '🇨🇳' },
  { code: 'zh-Hant', name: 'Traditional Chinese', nativeName: '繁體中文', flag: '🇹🇼' },
];

/**
 * Languages an owner can ADD to their storefront via the Storefront Designer — the 9 already-supported
 * storefront languages plus a broad catalogue of others. Picking one that the storefront doesn't have yet
 * triggers a full auto-translation (UI strings bundle + content + car categories) into it, server-side, so
 * the new language becomes fully usable without any frontend rebuild. Chinese keeps the canonical
 * `zh-Hans`/`zh-Hant` codes (DB content keying); the server maps them to the storefront's `zh-CN`/`zh-TW`
 * for the UI bundle. (RTL languages — ar/he/fa/ur — translate fine; storefront RTL layout is a follow-up.)
 */
export const ADDABLE_LANGUAGES: GuiLanguage[] = [
  ...STOREFRONT_LANGUAGES,
  { code: 'ar', name: 'Arabic', nativeName: 'العربية', flag: '🇸🇦' },
  { code: 'hi', name: 'Hindi', nativeName: 'हिन्दी', flag: '🇮🇳' },
  { code: 'ja', name: 'Japanese', nativeName: '日本語', flag: '🇯🇵' },
  { code: 'ko', name: 'Korean', nativeName: '한국어', flag: '🇰🇷' },
  { code: 'nl', name: 'Dutch', nativeName: 'Nederlands', flag: '🇳🇱' },
  { code: 'pl', name: 'Polish', nativeName: 'Polski', flag: '🇵🇱' },
  { code: 'tr', name: 'Turkish', nativeName: 'Türkçe', flag: '🇹🇷' },
  { code: 'uk', name: 'Ukrainian', nativeName: 'Українська', flag: '🇺🇦' },
  { code: 'vi', name: 'Vietnamese', nativeName: 'Tiếng Việt', flag: '🇻🇳' },
  { code: 'th', name: 'Thai', nativeName: 'ไทย', flag: '🇹🇭' },
  { code: 'id', name: 'Indonesian', nativeName: 'Bahasa Indonesia', flag: '🇮🇩' },
  { code: 'sv', name: 'Swedish', nativeName: 'Svenska', flag: '🇸🇪' },
  { code: 'el', name: 'Greek', nativeName: 'Ελληνικά', flag: '🇬🇷' },
  { code: 'he', name: 'Hebrew', nativeName: 'עברית', flag: '🇮🇱' },
  { code: 'fa', name: 'Persian', nativeName: 'فارسی', flag: '🇮🇷' },
  { code: 'cs', name: 'Czech', nativeName: 'Čeština', flag: '🇨🇿' },
  { code: 'ro', name: 'Romanian', nativeName: 'Română', flag: '🇷🇴' },
  { code: 'hu', name: 'Hungarian', nativeName: 'Magyar', flag: '🇭🇺' },
  { code: 'da', name: 'Danish', nativeName: 'Dansk', flag: '🇩🇰' },
  { code: 'fi', name: 'Finnish', nativeName: 'Suomi', flag: '🇫🇮' },
  { code: 'nb', name: 'Norwegian', nativeName: 'Norsk', flag: '🇳🇴' },
  { code: 'uz', name: 'Uzbek', nativeName: "O'zbek", flag: '🇺🇿' },
  { code: 'hy', name: 'Armenian', nativeName: 'Հայերեն', flag: '🇦🇲' },
  { code: 'ka', name: 'Georgian', nativeName: 'ქართული', flag: '🇬🇪' },
  { code: 'ur', name: 'Urdu', nativeName: 'اردو', flag: '🇵🇰' },
];

const supportedLanguages = GUI_LANGUAGES.map((l) => l.code);

// Lazy loaders — each dynamic import() becomes its own webpack chunk, loaded only when used.
const loaders: Record<string, () => Promise<any>> = {
  es: () => import('./locales/es/common.json'),
  de: () => import('./locales/de/common.json'),
  ru: () => import('./locales/ru/common.json'),
  fr: () => import('./locales/fr/common.json'),
  pt: () => import('./locales/pt/common.json'),
  tr: () => import('./locales/tr/common.json'),
  uk: () => import('./locales/uk/common.json'),
  fa: () => import('./locales/fa/common.json'),
  hy: () => import('./locales/hy/common.json'),
  ka: () => import('./locales/ka/common.json'),
  ur: () => import('./locales/ur/common.json'),
  uz: () => import('./locales/uz/common.json'),
  'zh-Hans': () => import('./locales/zh-Hans/common.json'),
  'zh-Hant': () => import('./locales/zh-Hant/common.json'),
};

const loaded = new Set<string>(['en']);

// Fetch a locale's resource bundle (once) and register it with i18next.
const ensureLanguage = async (lng: string): Promise<void> => {
  if (loaded.has(lng) || !loaders[lng]) return;
  try {
    const mod = await loaders[lng]();
    i18n.addResourceBundle(lng, 'translation', mod.default ?? mod, true, true);
    loaded.add(lng);
  } catch (e) {
    // Network/chunk error — i18next keeps falling back to English.
  }
};

/**
 * Make sure a language's strings are available WITHOUT switching the UI to it. Needed when a document is
 * rendered in a different language than the screen — e.g. the rental-agreement PDF, whose language comes
 * from the company's country, not from the person filling the form. Without this the lazy bundle would
 * still be unloaded and `getFixedT(lng)` would quietly hand back English.
 */
export const loadLanguageBundle = async (lng: string): Promise<void> => {
  await ensureLanguage(lng);
};

// Default language is English. Saved-language preference still wins so users
// who explicitly switched keep their choice.
const getSavedLanguage = (): string => {
  try {
    const saved = localStorage.getItem(LANGUAGE_KEY);
    if (saved && supportedLanguages.includes(saved)) {
      return saved;
    }
  } catch (e) {
    // localStorage not available
  }
  return 'en';
};

void i18n
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: en },
    },
    lng: 'en', // start synchronously in English; switch below if a non-en language is saved
    fallbackLng: 'en',
    interpolation: {
      escapeValue: false,
    },
    // Render with the fallback while a lazy locale loads instead of suspending.
    react: { useSuspense: false },
  });

/**
 * Put the chosen language on <html> — `lang` for the browser and screen readers, `dir` for layout.
 * `dir` is what makes Persian and Urdu lay out right-to-left across the WHOLE app: postcss-rtlcss
 * (see postcss.config.js) emits a `[dir="rtl"]` variant of every direction-dependent rule in the
 * bundle, and they only take effect once this attribute is set.
 */
const applyDocumentLanguage = (lng: string): void => {
  const el = document.documentElement;
  el.lang = lng;
  el.dir = dirForLanguage(lng);
};

// Any language change (saved-preference boot OR a user switching) lazy-loads the bundle
// if needed, then re-emits so subscribed components re-render in the new language.
i18n.on('languageChanged', (lng: string) => {
  try {
    localStorage.setItem(LANGUAGE_KEY, lng);
  } catch (e) {
    // localStorage not available
  }
  applyDocumentLanguage(lng);
  if (!loaded.has(lng) && loaders[lng]) {
    void ensureLanguage(lng).then(() => i18n.emit('languageChanged', lng));
  }
});

// Apply the saved language (loads its chunk, then switches) after the synchronous en init.
const saved = getSavedLanguage();
applyDocumentLanguage(saved); // before the async switch, so an RTL reload never flashes left-to-right
if (saved !== 'en') {
  void i18n.changeLanguage(saved);
}

export default i18n;